import { useId, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence } from "framer-motion";
import {
  Archive,
  Ban,
  Building2,
  CalendarPlus,
  CheckCircle2,
  Download,
  Lightbulb,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Tag,
  Trash2,
  X,
} from "lucide-react";
import { useToast } from "@/lib/toast";
import { useLeadDrawer } from "@/lib/leadDrawer";
import { Dialog } from "@/components/common/Dialog";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { OverflowMenu } from "@/components/common/OverflowMenu";
import { LoadingState, ErrorState } from "@/components/common/States";
import { StageBadge, PrioridadeBadge, NaoContatarBadge } from "@/components/common/Badges";
import { ScoreBadge } from "@/components/leads/ScoreBadge";
import { AdiarRapido } from "@/components/leads/AdiarRapido";
import { ScriptsSugeridos } from "@/components/leads/ScriptsSugeridos";
import { RegistrarTentativa } from "@/components/leads/RegistrarTentativa";
import {
  fetchAppUsers,
  fetchContactAttempts,
  fetchLeadById,
  fetchLeadStageEvents,
  fetchSupressaoAtivaPorLead,
  updateLead,
  type LeadPatch,
} from "@/lib/leads";
import { fetchLeadScore } from "@/lib/insights";
import { excluirDadosTitular, exportarDadosTitular, marcarNaoContatar, revogarNaoContatar } from "@/lib/lgpd";
import { downloadJson } from "@/lib/csv";
import {
  formatDateTime,
  mensagemPadraoWhatsapp,
  montarDadosEventoCalendar,
  montarUrlGoogleCalendar,
  paraInputDatetimeLocal,
  telHref,
  waHref,
} from "@/lib/format";
import {
  CONTACT_RESULT_LABELS,
  LEAD_PRIORIDADES,
  LEAD_PRIORIDADE_LABELS,
  LEAD_STAGE_LABELS,
} from "@/lib/types";
import type { AdiarLeadResultado, Lead } from "@/lib/types";

export function LeadDrawer() {
  const { leadId, closeLead } = useLeadDrawer();
  return (
    <AnimatePresence>
      {leadId && <LeadDrawerInner key={leadId} leadId={leadId} onClose={closeLead} />}
    </AnimatePresence>
  );
}

function LeadDrawerInner({ leadId, onClose }: { leadId: string; onClose: () => void }) {
  const titleId = useId();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const leadQuery = useQuery({ queryKey: ["lead", leadId], queryFn: () => fetchLeadById(leadId) });
  const attemptsQuery = useQuery({
    queryKey: ["contactAttempts", leadId],
    queryFn: () => fetchContactAttempts(leadId),
  });
  const eventsQuery = useQuery({
    queryKey: ["stageEvents", leadId],
    queryFn: () => fetchLeadStageEvents(leadId),
  });
  const usersQuery = useQuery({ queryKey: ["appUsers"], queryFn: fetchAppUsers });
  const scoreQuery = useQuery({ queryKey: ["leadScore", leadId], queryFn: () => fetchLeadScore(leadId) });

  const [editando, setEditando] = useState(false);
  const [confirmandoLigar, setConfirmandoLigar] = useState(false);
  const [confirmandoWhatsapp, setConfirmandoWhatsapp] = useState(false);
  const [mensagemWhatsapp, setMensagemWhatsapp] = useState<string | null>(null);
  const [mostrarNaoContatarForm, setMostrarNaoContatarForm] = useState(false);
  const [motivoNaoContatar, setMotivoNaoContatar] = useState("");
  const [mostrarExcluir, setMostrarExcluir] = useState(false);
  const [erroAcao, setErroAcao] = useState<string | null>(null);

  function invalidateListas() {
    queryClient.invalidateQueries({ queryKey: ["filaHoje"] });
    queryClient.invalidateQueries({ queryKey: ["kanban"] });
    queryClient.invalidateQueries({ queryKey: ["leadsSearch"] });
  }

  function onLeadAtualizadoLocal(lead: Lead) {
    queryClient.setQueryData(["lead", leadId], (old: unknown) =>
      old ? { ...(old as object), ...lead } : lead,
    );
    queryClient.invalidateQueries({ queryKey: ["contactAttempts", leadId] });
    queryClient.invalidateQueries({ queryKey: ["stageEvents", leadId] });
    invalidateListas();
  }

  function onAdiado(_resultado: AdiarLeadResultado) {
    queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
    queryClient.invalidateQueries({ queryKey: ["leadsParados"] });
    invalidateListas();
  }

  const marcarNaoContatarMutation = useMutation({
    mutationFn: (motivo: string) => marcarNaoContatar(leadId, motivo),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
      invalidateListas();
      setMostrarNaoContatarForm(false);
      setMotivoNaoContatar("");
      showToast({ message: "Lead marcado como não contatar.", tone: "success" });
    },
    onError: (e: unknown) => setErroAcao((e as Error).message),
  });

  const revogarMutation = useMutation({
    mutationFn: (supressaoId: string) => revogarNaoContatar(supressaoId, "Revogado pela equipe na ficha do lead."),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lead", leadId] });
      invalidateListas();
      showToast({ message: "Supressão revogada.", tone: "success" });
    },
    onError: (e: unknown) => setErroAcao((e as Error).message),
  });

  const excluirMutation = useMutation({
    mutationFn: () => excluirDadosTitular(leadId, true, "Pedido de exclusão registrado pela equipe."),
    onSuccess: () => {
      invalidateListas();
      setMostrarExcluir(false);
      showToast({ message: "Dados do titular excluídos.", tone: "success" });
      onClose();
    },
    onError: (e: unknown) => setErroAcao((e as Error).message),
  });

  const exportarMutation = useMutation({
    mutationFn: () => exportarDadosTitular(leadId),
    onSuccess: (data) => {
      downloadJson(`lead-${leadId}.json`, data);
      showToast({ message: "Exportação gerada.", tone: "success" });
    },
    onError: (e: unknown) => setErroAcao((e as Error).message),
  });

  const lead = leadQuery.data;

  return (
    <Dialog titleId={titleId} onClose={onClose} variant="drawer">
      <div className="drawer__header">
        <h2 id={titleId}>{lead ? lead.empresa : "Carregando…"}</h2>
        <button type="button" className="btn btn--ghost btn--icon" onClick={onClose} aria-label="Fechar ficha do lead">
          <X className="icon" size={20} aria-hidden="true" />
        </button>
      </div>

      {lead?.nao_contatar && (
        <div className="banner-danger" role="alert">
          <Ban className="icon" size={18} aria-hidden="true" />
          Este lead está marcado como <strong>Não contatar</strong>. Qualquer contato exige confirmação extra.
        </div>
      )}

      <div className="drawer__body">
        {leadQuery.isLoading && <LoadingState label="Carregando ficha do lead…" />}
        {leadQuery.isError && (
          <ErrorState message={(leadQuery.error as Error).message} onRetry={() => leadQuery.refetch()} />
        )}

        {lead && (
          <>
            <section style={{ marginBottom: 24 }}>
              <div className="chip-group" style={{ marginBottom: 12 }}>
                <StageBadge etapa={lead.etapa} />
                <PrioridadeBadge prioridade={lead.prioridade} />
                {lead.nao_contatar && <NaoContatarBadge />}
                <ScoreBadge score={scoreQuery.data} />
              </div>

              <div className="field-row">
                <Building2 className="icon" size={16} aria-hidden="true" />
                <span>{lead.ramo ?? "Ramo não informado"}</span>
              </div>
              <div className="field-row">
                <MapPin className="icon" size={16} aria-hidden="true" />
                <span>
                  {lead.cidade ?? "Cidade não informada"}
                  {lead.uf ? ` / ${lead.uf}` : ""}
                </span>
              </div>
              {lead.lote && (
                <div className="field-row">
                  <Archive className="icon" size={16} aria-hidden="true" />
                  <span>Lote de origem: {lead.lote.nome}</span>
                </div>
              )}
              {lead.tags.length > 0 && (
                <div className="field-row">
                  <Tag className="icon" size={16} aria-hidden="true" />
                  <div className="chip-group">
                    {lead.tags.map((t) => (
                      <span key={t} className="badge badge--neutral">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <p style={{ marginTop: 12 }}>
                Responsável: {lead.responsavel?.nome ?? "Sem responsável"} · Próximo contato:{" "}
                {formatDateTime(lead.proximo_contato_em)}
              </p>

              {lead.reuniao_em && (
                <p>
                  Reunião: {formatDateTime(lead.reuniao_em)}
                  {" · "}
                  <a
                    href={montarUrlGoogleCalendar(montarDadosEventoCalendar(lead)!)}
                    target="_blank"
                    rel="noreferrer"
                    style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                  >
                    <CalendarPlus className="icon" size={14} aria-hidden="true" />
                    Adicionar ao Google Agenda
                  </a>
                </p>
              )}

              <div style={{ marginTop: 8 }}>
                <AdiarRapido
                  leadId={lead.id}
                  empresa={lead.empresa}
                  onAdiado={onAdiado}
                  disabled={lead.nao_contatar || Boolean(lead.arquivado_em)}
                />
              </div>
            </section>

            <section style={{ marginBottom: 24 }}>
              <h3>Contato</h3>
              <div className="field-row">
                <Phone className="icon" size={16} aria-hidden="true" />
                <span>{lead.telefone ?? "—"}</span>
              </div>
              <p>Pessoa de contato: {lead.pessoa_contato ?? "—"}</p>
              <div className="field-row">
                <Mail className="icon" size={16} aria-hidden="true" />
                <span>{lead.email ?? "—"}</span>
              </div>
              <p>
                Site:{" "}
                {lead.site ? (
                  <a href={lead.site} target="_blank" rel="noreferrer">
                    {lead.site}
                  </a>
                ) : (
                  "—"
                )}
              </p>

              {lead.telefone && (
                <div className="fila-item__actions" style={{ marginTop: 8 }}>
                  <a
                    href={telHref(lead.telefone)}
                    className="btn btn--primary btn--sm"
                    onClick={(e) => {
                      if (lead.nao_contatar) {
                        e.preventDefault();
                        setConfirmandoLigar(true);
                      }
                    }}
                  >
                    <Phone className="icon" size={16} aria-hidden="true" />
                    Ligar
                  </a>

                  {lead.canal_permitido === "ligacao_e_whatsapp" ? (
                    <a
                      href={waHref(
                        lead.telefone,
                        mensagemWhatsapp ?? mensagemPadraoWhatsapp(lead.empresa, lead.pessoa_contato),
                      )}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn--secondary btn--sm"
                      onClick={(e) => {
                        if (lead.nao_contatar) {
                          e.preventDefault();
                          setConfirmandoWhatsapp(true);
                        }
                      }}
                    >
                      <MessageCircle className="icon" size={16} aria-hidden="true" />
                      WhatsApp
                    </a>
                  ) : (
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      disabled
                      title="WhatsApp libera automaticamente após uma resposta registrada (atendeu/reagendou)."
                    >
                      <MessageCircle className="icon" size={16} aria-hidden="true" />
                      WhatsApp (bloqueado)
                    </button>
                  )}
                </div>
              )}

              {lead.canal_permitido === "ligacao_e_whatsapp" && lead.telefone && (
                <div className="field" style={{ marginTop: 8 }}>
                  <label htmlFor="mensagem-whatsapp">Mensagem do WhatsApp</label>
                  <textarea
                    id="mensagem-whatsapp"
                    rows={2}
                    value={mensagemWhatsapp ?? mensagemPadraoWhatsapp(lead.empresa, lead.pessoa_contato)}
                    onChange={(e) => setMensagemWhatsapp(e.target.value)}
                  />
                </div>
              )}
            </section>

            <section style={{ marginBottom: 24 }}>
              <h3>
                <CheckCircle2 className="icon" size={16} aria-hidden="true" style={{ marginRight: 6, verticalAlign: -2 }} />
                Registrar tentativa
              </h3>
              <RegistrarTentativa lead={lead} onLeadAtualizado={onLeadAtualizadoLocal} startExpanded />
            </section>

            <section style={{ marginBottom: 24 }}>
              <h3>Scripts sugeridos</h3>
              <ScriptsSugeridos ramo={lead.ramo} />
            </section>

            {lead.etapa === "cliente_fechado" && (
              <section style={{ marginBottom: 24 }}>
                <h3>
                  <Lightbulb className="icon" size={16} aria-hidden="true" style={{ marginRight: 6, verticalAlign: -2 }} />
                  O que funcionou
                </h3>
                <NotaFechamento lead={lead} onSalvo={onLeadAtualizadoLocal} />
              </section>
            )}

            <section style={{ marginBottom: 24 }}>
              <h3>Histórico de tentativas</h3>
              {attemptsQuery.isLoading && <LoadingState label="Carregando histórico…" />}
              {attemptsQuery.data?.length === 0 && <p className="field-hint">Nenhuma tentativa registrada ainda.</p>}
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {attemptsQuery.data?.map((a) => {
                  const positivo = a.resultado === "atendeu" || a.resultado === "pediu_retorno";
                  const Icon = a.canal === "whatsapp" ? MessageCircle : Phone;
                  return (
                    <li key={a.id} className="timeline-item" style={{ opacity: a.anulado_em ? 0.5 : 1 }}>
                      <span
                        className={`timeline-item__icon${
                          a.canal === "whatsapp" && positivo ? " timeline-item__icon--positive" : ""
                        }`}
                        aria-hidden="true"
                      >
                        <Icon size={16} />
                      </span>
                      <div className="timeline-item__body">
                        <strong>{CONTACT_RESULT_LABELS[a.resultado]}</strong> por{" "}
                        {a.canal === "ligacao" ? "ligação" : "WhatsApp"} · {formatDateTime(a.ocorreu_em)} ·{" "}
                        {a.autor?.nome ?? "—"}
                        {a.nota && <p>{a.nota}</p>}
                        {a.anulado_em && <p className="field-hint">Anulada em {formatDateTime(a.anulado_em)}</p>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>

            <section style={{ marginBottom: 24 }}>
              <h3>Histórico de etapas</h3>
              {eventsQuery.isLoading && <LoadingState label="Carregando histórico…" />}
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {eventsQuery.data?.map((ev) => (
                  <li key={ev.id} className="field-hint" style={{ marginBottom: 4 }}>
                    {formatDateTime(ev.ocorreu_em)} — {ev.de ? LEAD_STAGE_LABELS[ev.de] : "criado"} →{" "}
                    {LEAD_STAGE_LABELS[ev.para]}
                    {ev.motivo?.rotulo && ` (${ev.motivo.rotulo})`}
                    {ev.autor?.nome && ` · ${ev.autor.nome}`}
                  </li>
                ))}
              </ul>
            </section>

            <section style={{ marginBottom: 24 }}>
              <div className="utility-row">
                <h3 style={{ margin: 0 }}>Dados do lead</h3>
                <div className="spacer" />
                <button type="button" className="btn btn--secondary btn--sm" onClick={() => setEditando((v) => !v)}>
                  {editando ? "Cancelar edição" : "Editar"}
                </button>
              </div>
              {editando ? (
                <EditarLeadForm
                  lead={lead}
                  responsaveis={usersQuery.data ?? []}
                  onSalvo={(atualizado) => {
                    onLeadAtualizadoLocal(atualizado);
                    setEditando(false);
                  }}
                  onCancelar={() => setEditando(false)}
                />
              ) : (
                <p className="field-hint">
                  Origem: {lead.origem_fonte} — {lead.origem_detalhe} (coletado em {formatDateTime(lead.origem_coletada_em)})
                </p>
              )}
            </section>

            <section>
              <h3>LGPD</h3>
              {erroAcao && (
                <p className="field-error" role="alert">
                  {erroAcao}
                </p>
              )}
              {!lead.nao_contatar ? (
                <>
                  {!mostrarNaoContatarForm ? (
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      onClick={() => setMostrarNaoContatarForm(true)}
                    >
                      Marcar como não contatar
                    </button>
                  ) : (
                    <div className="field">
                      <label htmlFor="motivo-nao-contatar">Motivo (obrigatório)</label>
                      <textarea
                        id="motivo-nao-contatar"
                        rows={2}
                        value={motivoNaoContatar}
                        onChange={(e) => setMotivoNaoContatar(e.target.value)}
                      />
                      <div className="fila-item__actions">
                        <button
                          type="button"
                          className="btn btn--danger btn--sm"
                          disabled={motivoNaoContatar.trim() === "" || marcarNaoContatarMutation.isPending}
                          onClick={() => marcarNaoContatarMutation.mutate(motivoNaoContatar.trim())}
                        >
                          Confirmar
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => setMostrarNaoContatarForm(false)}
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <RevogarNaoContatarAcao
                  leadId={lead.id}
                  onRevogar={(supressaoId) => revogarMutation.mutate(supressaoId)}
                  pending={revogarMutation.isPending}
                />
              )}

              <div className="fila-item__actions" style={{ marginTop: 12, alignItems: "center" }}>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => exportarMutation.mutate()}
                  disabled={exportarMutation.isPending}
                >
                  <Download className="icon" size={16} aria-hidden="true" />
                  Exportar dados (JSON)
                </button>
                <div className="spacer" />
                {/* Exclusao de dados do titular (LGPD) NUNCA e botao solto:
                    fica atras do menu de mais acoes, e a confirmacao por
                    frase digitada do ConfirmDialog e a segunda etapa depois
                    de abrir o menu e escolher o item. */}
                <OverflowMenu
                  label="Mais ações do lead"
                  items={[
                    {
                      key: "excluir",
                      label: "Excluir dados do titular",
                      icon: <Trash2 className="icon" size={16} aria-hidden="true" />,
                      tone: "danger",
                      onSelect: () => setMostrarExcluir(true),
                    },
                  ]}
                />
              </div>
            </section>
          </>
        )}
      </div>

      <AnimatePresence>
        {confirmandoLigar && lead?.telefone && (
          <ConfirmDialog
            key="confirmar-ligar"
            title="Confirmar ligação"
            description="Este lead está marcado como Não Contatar. Tem certeza de que deseja ligar mesmo assim?"
            confirmLabel="Ligar mesmo assim"
            tone="danger"
            onConfirm={() => {
              setConfirmandoLigar(false);
              window.location.href = telHref(lead.telefone as string);
            }}
            onCancel={() => setConfirmandoLigar(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {confirmandoWhatsapp && lead?.telefone && (
          <ConfirmDialog
            key="confirmar-whatsapp"
            title="Confirmar WhatsApp"
            description="Este lead está marcado como Não Contatar. Tem certeza de que deseja enviar mensagem mesmo assim?"
            confirmLabel="Abrir WhatsApp mesmo assim"
            tone="danger"
            onConfirm={() => {
              setConfirmandoWhatsapp(false);
              window.open(
                waHref(lead.telefone as string, mensagemWhatsapp ?? mensagemPadraoWhatsapp(lead.empresa, lead.pessoa_contato)),
                "_blank",
              );
            }}
            onCancel={() => setConfirmandoWhatsapp(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {mostrarExcluir && (
          <ConfirmDialog
            key="confirmar-excluir"
            title="Excluir dados do titular"
            description={
              'Esta ação é IRREVERSÍVEL: apaga o lead, tentativas e histórico de etapas, e expurga o payload de importação. ' +
              "A supressão (não contatar) é preservada para o titular não voltar a ser contatado."
            }
            confirmLabel="Excluir definitivamente"
            tone="danger"
            requireTypedPhrase="EXCLUIR"
            busy={excluirMutation.isPending}
            errorMessage={erroAcao}
            onConfirm={() => excluirMutation.mutate()}
            onCancel={() => setMostrarExcluir(false)}
          />
        )}
      </AnimatePresence>
    </Dialog>
  );
}

function RevogarNaoContatarAcao({
  leadId,
  onRevogar,
  pending,
}: {
  leadId: string;
  onRevogar: (supressaoId: string) => void;
  pending: boolean;
}) {
  const supressaoQuery = useQuery({
    queryKey: ["naoContatarAtiva", leadId],
    queryFn: () => fetchSupressaoAtivaPorLead(leadId),
  });

  if (supressaoQuery.isLoading) return <LoadingState label="Verificando supressão…" />;
  if (!supressaoQuery.data) {
    return (
      <p className="field-hint">
        Lead suprimido por telefone/empresa+cidade de outro registro — revogue pela lista de supressão.
      </p>
    );
  }

  return (
    <button
      type="button"
      className="btn btn--secondary btn--sm"
      disabled={pending}
      onClick={() => onRevogar(supressaoQuery.data!.id)}
    >
      Revogar não contatar
    </button>
  );
}

/** "O que funcionou" — leads.nota_fechamento. So mostrada/editavel quando
 * etapa = cliente_fechado (decisao de produto): antes disso a pergunta nao
 * faz sentido, e nao ha check no banco forcando isso (e campo de nota
 * qualitativa, sem validacao rigida de proposito). */
function NotaFechamento({ lead, onSalvo }: { lead: Lead; onSalvo: (lead: Lead) => void }) {
  const [nota, setNota] = useState(lead.nota_fechamento ?? "");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const alterado = nota !== (lead.nota_fechamento ?? "");

  async function salvar() {
    setSalvando(true);
    setErro(null);
    try {
      const atualizado = await updateLead(lead.id, { nota_fechamento: nota.trim() || null });
      onSalvo(atualizado);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div>
      <div className="field">
        <label htmlFor="nota-fechamento" className="visually-hidden">
          O que funcionou para fechar este cliente
        </label>
        <textarea
          id="nota-fechamento"
          rows={3}
          placeholder="O que funcionou para fechar esse cliente? Ajuda a repetir no próximo."
          value={nota}
          onChange={(e) => setNota(e.target.value)}
        />
      </div>
      {erro && (
        <p className="field-error" role="alert">
          {erro}
        </p>
      )}
      <button
        type="button"
        className="btn btn--secondary btn--sm"
        disabled={!alterado || salvando}
        onClick={salvar}
      >
        {salvando ? "Salvando…" : "Salvar"}
      </button>
    </div>
  );
}

function EditarLeadForm({
  lead,
  responsaveis,
  onSalvo,
  onCancelar,
}: {
  lead: Lead;
  responsaveis: { id: string; nome: string }[];
  onSalvo: (lead: Lead) => void;
  onCancelar: () => void;
}) {
  const [form, setForm] = useState({
    empresa: lead.empresa,
    ramo: lead.ramo ?? "",
    cidade: lead.cidade ?? "",
    uf: lead.uf ?? "",
    telefone: lead.telefone ?? "",
    pessoa_contato: lead.pessoa_contato ?? "",
    email: lead.email ?? "",
    site: lead.site ?? "",
    prioridade: lead.prioridade,
    responsavel_id: lead.responsavel_id ?? "",
    reuniao_em: paraInputDatetimeLocal(lead.reuniao_em),
  });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function salvar() {
    setSalvando(true);
    setErro(null);
    try {
      const patch: LeadPatch = {
        empresa: form.empresa.trim(),
        ramo: form.ramo.trim() || null,
        cidade: form.cidade.trim() || null,
        uf: form.uf.trim() || null,
        telefone: form.telefone.trim() || null,
        pessoa_contato: form.pessoa_contato.trim() || null,
        email: form.email.trim() || null,
        site: form.site.trim() || null,
        prioridade: form.prioridade,
        responsavel_id: form.responsavel_id || null,
        reuniao_em: form.reuniao_em ? new Date(form.reuniao_em).toISOString() : null,
      };
      const atualizado = await updateLead(lead.id, patch);
      onSalvo(atualizado);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div>
      <div className="form-row">
        <div className="field">
          <label htmlFor="edit-empresa">Empresa</label>
          <input
            id="edit-empresa"
            type="text"
            value={form.empresa}
            onChange={(e) => setForm((f) => ({ ...f, empresa: e.target.value }))}
          />
        </div>
        <div className="field">
          <label htmlFor="edit-ramo">Ramo</label>
          <input
            id="edit-ramo"
            type="text"
            value={form.ramo}
            onChange={(e) => setForm((f) => ({ ...f, ramo: e.target.value }))}
          />
        </div>
      </div>
      <div className="form-row">
        <div className="field">
          <label htmlFor="edit-cidade">Cidade</label>
          <input
            id="edit-cidade"
            type="text"
            value={form.cidade}
            onChange={(e) => setForm((f) => ({ ...f, cidade: e.target.value }))}
          />
        </div>
        <div className="field">
          <label htmlFor="edit-uf">UF</label>
          <input
            id="edit-uf"
            type="text"
            maxLength={2}
            value={form.uf}
            onChange={(e) => setForm((f) => ({ ...f, uf: e.target.value.toUpperCase() }))}
          />
        </div>
      </div>
      <div className="form-row">
        <div className="field">
          <label htmlFor="edit-telefone">Telefone</label>
          <input
            id="edit-telefone"
            type="tel"
            value={form.telefone}
            onChange={(e) => setForm((f) => ({ ...f, telefone: e.target.value }))}
          />
        </div>
        <div className="field">
          <label htmlFor="edit-pessoa">Pessoa de contato</label>
          <input
            id="edit-pessoa"
            type="text"
            value={form.pessoa_contato}
            onChange={(e) => setForm((f) => ({ ...f, pessoa_contato: e.target.value }))}
          />
        </div>
      </div>
      <div className="form-row">
        <div className="field">
          <label htmlFor="edit-email">E-mail</label>
          <input
            id="edit-email"
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          />
        </div>
        <div className="field">
          <label htmlFor="edit-site">Site</label>
          <input
            id="edit-site"
            type="url"
            value={form.site}
            onChange={(e) => setForm((f) => ({ ...f, site: e.target.value }))}
          />
        </div>
      </div>
      <div className="form-row">
        <div className="field">
          <label htmlFor="edit-prioridade">Prioridade</label>
          <select
            id="edit-prioridade"
            value={form.prioridade}
            onChange={(e) => setForm((f) => ({ ...f, prioridade: e.target.value as Lead["prioridade"] }))}
          >
            {LEAD_PRIORIDADES.map((p) => (
              <option key={p} value={p}>
                {LEAD_PRIORIDADE_LABELS[p]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="edit-responsavel">Responsável</label>
          <select
            id="edit-responsavel"
            value={form.responsavel_id}
            onChange={(e) => setForm((f) => ({ ...f, responsavel_id: e.target.value }))}
          >
            <option value="">Sem responsável</option>
            {responsaveis.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nome}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="field">
          <label htmlFor="edit-reuniao">Data/hora da reunião</label>
          <input
            id="edit-reuniao"
            type="datetime-local"
            value={form.reuniao_em}
            onChange={(e) => setForm((f) => ({ ...f, reuniao_em: e.target.value }))}
          />
          <p className="field-hint">Corrija aqui se a reunião foi remarcada.</p>
        </div>
      </div>

      {erro && (
        <p className="field-error" role="alert">
          {erro}
        </p>
      )}

      <div className="fila-item__actions">
        <button type="button" className="btn btn--primary btn--sm" disabled={salvando} onClick={salvar}>
          Salvar
        </button>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onCancelar}>
          Cancelar
        </button>
      </div>
    </div>
  );
}
